'use client';

// Third Party
import { c as _c } from "react/compiler-runtime";
import 'react-dates/initialize';
import * as React from 'react';
import { DayPickerRangeController, DayPickerRangeControllerShape, FocusedInputShape } from 'react-dates';
import moment from 'moment';

// Utils
import { FeatureKey } from '@gql';
import { formatDateMMMD } from '@utils/date-helpers';
import { formatWholeDollarAmount } from '@utils/money';
import { guestCapacity } from '@utils/constants/guest-capacity-thresholds';
import type { ListingBySlugPartial } from '@utils/imperative-node-queries';
import { useQuotes } from './quotes-context';

// UI
import { AnalyticsRef } from '@components/analytics-ref';
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { DayPickerTooltip, getDayPickerTooltipContent, useDayPickerTooltip } from '@components/day-picker-tooltip';
import { Dropdown } from '@components/dropdown';
import { Icon, IconNames } from '@components/icon';
import { Input } from '@components/form/input';
import { Link } from '@components/link';
import { ListingQuoteBreakdown } from '@components/listing-quote-breakdown';
import { Typography } from '@components/typography';
import { QuotesCompare } from './quotes-compare/quotes-compare';
export type Props = {
  cta: {
    label: string;
    /**
     * Lead-in text for the subtext line under the CTA. Paired with the
     * property manager's `companyName`. Comes from BHT-6145 comps.
     */
    subtext?: string;
    bookingUrl?: string;
    analyticsRef: string;
    showExternalIcon?: boolean;
  };
  listing: ListingBySlugPartial;
  nightlyPriceInfo: string | JSX.Element;
  shouldShowRatesAndAvailability: boolean;
};
interface IHandleDatesChange {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

// Cast DayPickerRangeController to ComponentType to resolve React JSX type conflicts
// This tells TypeScript to treat it as a valid React component that can accept props
const DayPickerComponent = DayPickerRangeController as React.ComponentType<DayPickerRangeControllerShape>;
export const QuotesCta: React.FC<Props> = t0 => {
  const $ = _c(202);
  const {
    cta,
    listing,
    shouldShowRatesAndAvailability
  } = t0;
  const [dateRangePickerInputElement, setDateRangePickerInputElement] = React.useState(null);
  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] = React.useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = moment();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const currentMoment = t1;
  const {
    availabilityDataLoading,
    quoteData,
    adultCount,
    setAdultCount,
    childCount,
    setChildCount,
    infantCount,
    setInfantCount,
    petCount,
    guestCountHumanReadableValue,
    guestCapacityReached,
    setPetCount,
    checkInDate,
    setCheckInDate,
    checkOutDate,
    setCheckOutDate,
    isDayBlocked,
    isStartDayBlocked,
    isEndDayBlocked,
    minimumNightsForSelectedDate,
    getMinNightsForHoverDate,
    datesPresent,
    datesInvalid,
    numberOfNights,
    quoteDataLoading,
    quoteValidationMessage
  } = useQuotes();
  let t2;
  if ($[1] !== listing.features) {
    t2 = listing.features.some(_temp);
    $[1] = listing.features;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const petsNotAllowed = t2;
  const [guestCountInputElement, setGuestCountInputElement] = React.useState(null);
  const [guestCountFocusedInput, setGuestCountFocusedInput] = React.useState(false);
  let t3;
  if ($[3] !== setCheckInDate || $[4] !== setCheckOutDate) {
    t3 = t4 => {
      const {
        startDate,
        endDate
      } = t4;
      setCheckInDate(startDate ? startDate.format("YYYY-MM-DD") : null);
      setCheckOutDate(endDate ? endDate.format("YYYY-MM-DD") : null);
    };
    $[3] = setCheckInDate;
    $[4] = setCheckOutDate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const onDatesChange = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setDateRangePickerFocusedInput(null);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onDropdownClickAway = t4;
  let t5;
  if ($[7] !== setCheckInDate || $[8] !== setCheckOutDate) {
    t5 = () => {
      setCheckInDate(null);
      setCheckOutDate(null);
      setDateRangePickerFocusedInput("startDate");
    };
    $[7] = setCheckInDate;
    $[8] = setCheckOutDate;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const onClearDatesClick = t5;
  let t6;
  if ($[10] !== checkInDate || $[11] !== checkOutDate || $[12] !== getMinNightsForHoverDate || $[13] !== isDayBlocked || $[14] !== isEndDayBlocked || $[15] !== isStartDayBlocked) {
    t6 = day => getDayPickerTooltipContent({
      day,
      getMinNightsForHoverDate,
      checkInDate,
      checkOutDate,
      isStartDayBlocked,
      isEndDayBlocked,
      isDayBlocked
    });
    $[10] = checkInDate;
    $[11] = checkOutDate;
    $[12] = getMinNightsForHoverDate;
    $[13] = isDayBlocked;
    $[14] = isEndDayBlocked;
    $[15] = isStartDayBlocked;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const getTooltipContent = t6;
  const {
    tooltipState,
    renderDayContents
  } = useDayPickerTooltip(getTooltipContent);
  const handleValueChange = _temp2;
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = el => setDateRangePickerInputElement(el);
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== checkInDate) {
    t8 = checkInDate ? formatDateMMMD(checkInDate) : undefined;
    $[18] = checkInDate;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setDateRangePickerFocusedInput("startDate");
    };
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = datesInvalid || false;
  let t11;
  if ($[21] !== t10 || $[22] !== t8) {
    t11 = <div className="min-w-[90px] py-1 border-primary-main border-r-2"><Input hasBorder={false} label="Check-in" name="checkInDate" value={t8} onFocus={t9} isInvalid={t10} autoComplete="off" hasCaret={false} readOnly={true} className="bg-white" inputClassName="bg-white text-primary-main" /></div>;
    $[21] = t10;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== checkOutDate) {
    t12 = checkOutDate ? formatDateMMMD(checkOutDate) : undefined;
    $[24] = checkOutDate;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== checkInDate) {
    t13 = () => {
      if (!checkInDate) {
        setDateRangePickerFocusedInput("startDate");
      } else {
        setDateRangePickerFocusedInput("endDate");
      }
    };
    $[26] = checkInDate;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = datesInvalid || false;
  let t15;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t14) {
    t15 = <div className="min-w-[90px] py-1"><Input hasBorder={false} label="Check-out" name="checkOutDate" value={t12} onFocus={t13} isInvalid={t14} autoComplete="off" hasCaret={false} readOnly={true} className="bg-white" inputClassName="bg-white text-primary-main" /></div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t11 || $[33] !== t15) {
    t16 = <div className="w-full flex lg:space-x-2 whitespace-nowrap">{t11}{t15}</div>;
    $[32] = t11;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = el_0 => setGuestCountInputElement(el_0);
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setGuestCountFocusedInput(true);
    };
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== guestCountHumanReadableValue) {
    t19 = <div ref={t17} className="min-w-[120px] w-full py-1 border-primary-main border-t-2"><Input hasBorder={false} label="Guests" name="guests" value={guestCountHumanReadableValue} onFocus={t18} autoComplete="off" readOnly={true} className="bg-white" inputClassName="bg-white text-primary-main" /></div>;
    $[37] = guestCountHumanReadableValue;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t16 || $[40] !== t19) {
    t20 = <div ref={t7} className="w-full flex flex-col mt-1 whitespace-nowrap border-primary-main border-[1.5px] rounded-lg bg-white">{t16}{t19}</div>;
    $[39] = t16;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const t21 = !!dateRangePickerFocusedInput;
  let t22;
  if ($[42] !== checkInDate) {
    t22 = checkInDate ? moment(checkInDate) : null;
    $[42] = checkInDate;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== checkOutDate) {
    t23 = checkOutDate ? moment(checkOutDate) : null;
    $[44] = checkOutDate;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== isDayBlocked) {
    t24 = day_0 => isDayBlocked(day_0);
    $[46] = isDayBlocked;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== isDayBlocked || $[49] !== isEndDayBlocked || $[50] !== isStartDayBlocked) {
    t25 = day_1 => isStartDayBlocked(day_1) || isEndDayBlocked(day_1) && !isDayBlocked(day_1);
    $[48] = isDayBlocked;
    $[49] = isEndDayBlocked;
    $[50] = isStartDayBlocked;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== availabilityDataLoading || $[53] !== dateRangePickerFocusedInput || $[54] !== getMinNightsForHoverDate || $[55] !== minimumNightsForSelectedDate || $[56] !== onDatesChange || $[57] !== renderDayContents || $[58] !== t22 || $[59] !== t23 || $[60] !== t24 || $[61] !== t25) {
    t26 = <div className="flex justify-start items-start w-full"><DayPickerComponent verticalBorderSpacing={0} enableOutsideDays={false} minDate={currentMoment} noBorder={true} numberOfMonths={1} initialVisibleMonth={null} focusedInput={dateRangePickerFocusedInput} onDatesChange={onDatesChange} onFocusChange={setDateRangePickerFocusedInput} startDate={t22} endDate={t23} isDayBlocked={t24} isOutsideRange={t25} hideKeyboardShortcutsPanel={true} disabled={availabilityDataLoading} minimumNights={minimumNightsForSelectedDate} daysViolatingMinNightsCanBeClicked={false} getMinNightsForHoverDate={getMinNightsForHoverDate} renderDayContents={renderDayContents} /></div>;
    $[52] = availabilityDataLoading;
    $[53] = dateRangePickerFocusedInput;
    $[54] = getMinNightsForHoverDate;
    $[55] = minimumNightsForSelectedDate;
    $[56] = onDatesChange;
    $[57] = renderDayContents;
    $[58] = t22;
    $[59] = t23;
    $[60] = t24;
    $[61] = t25;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== onClearDatesClick) {
    t27 = <div className="flex w-full justify-end pb-2 pr-2"><Button variant="text" onClick={onClearDatesClick}>Clear</Button></div>;
    $[63] = onClearDatesClick;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== dateRangePickerInputElement || $[66] !== t21 || $[67] !== t26 || $[68] !== t27) {
    t28 = <Dropdown dropdownWidth="full" reference={dateRangePickerInputElement} isOpen={t21} onClickAway={onDropdownClickAway} data-id="date-range-picker-dropdown" className="right-0 lg:right-auto absolute -mt-[59px]">{t26}{t27}</Dropdown>;
    $[65] = dateRangePickerInputElement;
    $[66] = t21;
    $[67] = t26;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => {
      setGuestCountFocusedInput(false);
    };
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="flex flex-col"><Typography variant="subtitle2" component="p">Adults</Typography><Typography variant="body2" component="p">Ages 13 or above</Typography></div>;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== setAdultCount) {
    t31 = () => setAdultCount(_temp3);
    $[72] = setAdultCount;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  const t32 = !adultCount || adultCount === 1;
  let t33;
  if ($[74] !== t31 || $[75] !== t32) {
    t33 = <ButtonIcon icon={IconNames.MINUS} ariaLabel="Remove adult" variant="text" iconSize="sm" color="gray" onClick={t31} disabled={t32} />;
    $[74] = t31;
    $[75] = t32;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  const t34 = adultCount || 1;
  let t35;
  if ($[77] !== setAdultCount) {
    t35 = e_0 => {
      handleValueChange(e_0, setAdultCount, guestCapacity.ADULTS);
    };
    $[77] = setAdultCount;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== listing.guestCapacity || $[80] !== t34 || $[81] !== t35) {
    t36 = <input type="number" aria-label="Number of adults" className="w-6 text-center" max={listing.guestCapacity} min={1} value={t34} data-testid="@quotes-form/adult-count" onChange={t35} />;
    $[79] = listing.guestCapacity;
    $[80] = t34;
    $[81] = t35;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  let t37;
  if ($[83] !== setAdultCount) {
    t37 = () => setAdultCount(_temp4);
    $[83] = setAdultCount;
    $[84] = t37;
  } else {
    t37 = $[84];
  }
  let t38;
  if ($[85] !== guestCapacityReached || $[86] !== t37) {
    t38 = <ButtonIcon icon={IconNames.PLUS} ariaLabel="Add adult" variant="text" iconSize="sm" color="gray" onClick={t37} disabled={guestCapacityReached} />;
    $[85] = guestCapacityReached;
    $[86] = t37;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] !== t33 || $[89] !== t36 || $[90] !== t38) {
    t39 = <div className="flex space-x-6 justify-between">{t30}<div className="flex space-x-2 justify-between">{t33}{t36}{t38}</div></div>;
    $[88] = t33;
    $[89] = t36;
    $[90] = t38;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className="flex flex-col"><Typography variant="subtitle2" component="p">Children</Typography><Typography variant="body2" component="p">Ages 2-12</Typography></div>;
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  let t41;
  if ($[93] !== setChildCount) {
    t41 = <ButtonIcon icon={IconNames.MINUS} ariaLabel="Remove child" variant="text" iconSize="sm" color="gray" onClick={() => setChildCount(_temp5)} />;
    $[93] = setChildCount;
    $[94] = t41;
  } else {
    t41 = $[94];
  }
  let t42;
  if ($[95] !== setChildCount) {
    t42 = e_1 => {
      handleValueChange(e_1, setChildCount, guestCapacity.CHILDREN);
    };
    $[95] = setChildCount;
    $[96] = t42;
  } else {
    t42 = $[96];
  }
  let t43;
  if ($[97] !== childCount || $[98] !== t42) {
    t43 = <input type="number" aria-label="Number of children" className="w-6 text-center" value={childCount} data-testid="@quotes-form/child-count" onChange={t42} />;
    $[97] = childCount;
    $[98] = t42;
    $[99] = t43;
  } else {
    t43 = $[99];
  }
  let t44;
  if ($[100] !== setChildCount) {
    t44 = () => setChildCount(_temp6);
    $[100] = setChildCount;
    $[101] = t44;
  } else {
    t44 = $[101];
  }
  let t45;
  if ($[102] !== guestCapacityReached || $[103] !== t44) {
    t45 = <ButtonIcon icon={IconNames.PLUS} ariaLabel="Add child" variant="text" iconSize="sm" color="gray" onClick={t44} disabled={guestCapacityReached} />;
    $[102] = guestCapacityReached;
    $[103] = t44;
    $[104] = t45;
  } else {
    t45 = $[104];
  }
  let t46;
  if ($[105] !== t41 || $[106] !== t43 || $[107] !== t45) {
    t46 = <div className="flex space-x-6 justify-between pt-4">{t40}<div className="flex space-x-2 justify-between">{t41}{t43}{t45}</div></div>;
    $[105] = t41;
    $[106] = t43;
    $[107] = t45;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  let t47;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <div className="flex flex-col"><Typography variant="subtitle2" component="p">Infants</Typography><Typography variant="body2" component="p">Under 2</Typography></div>;
    $[109] = t47;
  } else {
    t47 = $[109];
  }
  let t48;
  if ($[110] !== setInfantCount) {
    t48 = <ButtonIcon icon={IconNames.MINUS} ariaLabel="Remove infant" variant="text" iconSize="sm" color="gray" onClick={() => setInfantCount(_temp7)} />;
    $[110] = setInfantCount;
    $[111] = t48;
  } else {
    t48 = $[111];
  }
  let t49;
  if ($[112] !== setInfantCount) {
    t49 = e_2 => {
      handleValueChange(e_2, setInfantCount, guestCapacity.INFANTS);
    };
    $[112] = setInfantCount;
    $[113] = t49;
  } else {
    t49 = $[113];
  }
  let t50;
  if ($[114] !== infantCount || $[115] !== t49) {
    t50 = <input type="number" aria-label="Number of infants" className="w-6 text-center" value={infantCount} data-testid="@quotes-form/infant-count" onChange={t49} />;
    $[114] = infantCount;
    $[115] = t49;
    $[116] = t50;
  } else {
    t50 = $[116];
  }
  let t51;
  if ($[117] !== setInfantCount) {
    t51 = <ButtonIcon icon={IconNames.PLUS} ariaLabel="Add infant" variant="text" iconSize="sm" color="gray" onClick={() => setInfantCount(_temp8)} />;
    $[117] = setInfantCount;
    $[118] = t51;
  } else {
    t51 = $[118];
  }
  let t52;
  if ($[119] !== t48 || $[120] !== t50 || $[121] !== t51) {
    t52 = <div className="flex space-x-6 justify-between pt-4">{t47}<div className="flex space-x-2 justify-between">{t48}{t50}{t51}</div></div>;
    $[119] = t48;
    $[120] = t50;
    $[121] = t51;
    $[122] = t52;
  } else {
    t52 = $[122];
  }
  let t53;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <div className="flex flex-col justify-center"><Typography variant="subtitle2" component="p">Pets</Typography></div>;
    $[123] = t53;
  } else {
    t53 = $[123];
  }
  let t54;
  if ($[124] !== setPetCount) {
    t54 = () => setPetCount(_temp9);
    $[124] = setPetCount;
    $[125] = t54;
  } else {
    t54 = $[125];
  }
  let t55;
  if ($[126] !== petsNotAllowed || $[127] !== t54) {
    t55 = <ButtonIcon icon={IconNames.MINUS} ariaLabel="Remove pet" variant="text" iconSize="sm" color="gray" onClick={t54} disabled={petsNotAllowed} />;
    $[126] = petsNotAllowed;
    $[127] = t54;
    $[128] = t55;
  } else {
    t55 = $[128];
  }
  let t56;
  if ($[129] !== setPetCount) {
    t56 = e_3 => {
      handleValueChange(e_3, setPetCount, guestCapacity.PETS);
    };
    $[129] = setPetCount;
    $[130] = t56;
  } else {
    t56 = $[130];
  }
  let t57;
  if ($[131] !== petCount || $[132] !== petsNotAllowed || $[133] !== t56) {
    t57 = <input type="number" aria-label="Number of pets" className="w-6 text-center" value={petCount} data-testid="@quotes-form/pet-count" onChange={t56} disabled={petsNotAllowed} />;
    $[131] = petCount;
    $[132] = petsNotAllowed;
    $[133] = t56;
    $[134] = t57;
  } else {
    t57 = $[134];
  }
  let t58;
  if ($[135] !== setPetCount) {
    t58 = () => setPetCount(_temp10);
    $[135] = setPetCount;
    $[136] = t58;
  } else {
    t58 = $[136];
  }
  let t59;
  if ($[137] !== petsNotAllowed || $[138] !== t58) {
    t59 = <ButtonIcon icon={IconNames.PLUS} ariaLabel="Add pet" variant="text" iconSize="sm" color="gray" onClick={t58} disabled={petsNotAllowed} />;
    $[137] = petsNotAllowed;
    $[138] = t58;
    $[139] = t59;
  } else {
    t59 = $[139];
  }
  let t60;
  if ($[140] !== t55 || $[141] !== t57 || $[142] !== t59) {
    t60 = <div className="flex justify-between pt-4">{t53}<div className="flex space-x-2 justify-between">{t55}{t57}{t59}</div></div>;
    $[140] = t55;
    $[141] = t57;
    $[142] = t59;
    $[143] = t60;
  } else {
    t60 = $[143];
  }
  let t61;
  if ($[144] !== guestCountFocusedInput || $[145] !== guestCountInputElement || $[146] !== t39 || $[147] !== t46 || $[148] !== t52 || $[149] !== t60) {
    t61 = <Dropdown dropdownWidth="full" reference={guestCountInputElement} isOpen={guestCountFocusedInput} onClickAway={t29} data-id="guest-count-picker-dropdown" className="absolute p-4 mt-0 space-y-4 divide-y divide-gray-light text-left">{t39}{t46}{t52}{t60}</Dropdown>;
    $[144] = guestCountFocusedInput;
    $[145] = guestCountInputElement;
    $[146] = t39;
    $[147] = t46;
    $[148] = t52;
    $[149] = t60;
    $[150] = t61;
  } else {
    t61 = $[150];
  }
  let t62;
  if ($[151] !== cta.analyticsRef || $[152] !== cta.bookingUrl || $[153] !== cta.label || $[154] !== cta.showExternalIcon || $[155] !== cta.subtext || $[156] !== datesPresent || $[157] !== listing.account || $[158] !== quoteData || $[159] !== quoteDataLoading || $[160] !== shouldShowRatesAndAvailability) {
    t62 = shouldShowRatesAndAvailability && !quoteData || shouldShowRatesAndAvailability && quoteData?.bookingQuoteByListingAndDates.__typename == "ValidationError" || shouldShowRatesAndAvailability && !datesPresent ? <Button className="justify-center whitespace-nowrap h-12" onClick={() => {
      setDateRangePickerFocusedInput("startDate");
    }} isFullWidth={true} data-id={cta.analyticsRef} isLoading={quoteDataLoading}>{cta.label}</Button> : cta.bookingUrl && <AnalyticsRef name={cta.analyticsRef}><Link isButton={true} isFullWidth={true} to={cta.bookingUrl} target="_blank" rel="noopener noreferrer" className="h-12 text-primary-main" isLoading={quoteDataLoading}><span className="flex items-center justify-center gap-2">{cta.label}{cta.showExternalIcon && <Icon name={IconNames.ARROWUPRIGHTFROMSQUARE} size="sm" />}</span></Link>{cta.subtext && <div className="flex flex-row flex-wrap items-baseline justify-center gap-x-1 whitespace-nowrap pt-2 text-xs text-secondary-dark"><span className="font-light">{cta.subtext}</span><span className="font-medium">{listing.account.companyName}</span></div>}</AnalyticsRef>;
    $[151] = cta.analyticsRef;
    $[152] = cta.bookingUrl;
    $[153] = cta.label;
    $[154] = cta.showExternalIcon;
    $[155] = cta.subtext;
    $[156] = datesPresent;
    $[157] = listing.account;
    $[158] = quoteData;
    $[159] = quoteDataLoading;
    $[160] = shouldShowRatesAndAvailability;
    $[161] = t62;
  } else {
    t62 = $[161];
  }
  let t63;
  if ($[162] !== t62) {
    t63 = <div className="mt-5 w-full text-white">{t62}</div>;
    $[162] = t62;
    $[163] = t63;
  } else {
    t63 = $[163];
  }
  let t64;
  if ($[164] !== datesPresent || $[165] !== shouldShowRatesAndAvailability) {
    t64 = shouldShowRatesAndAvailability && datesPresent ? <div className="mt-4"><div className="mt-5"><QuotesCompare /></div></div> : null;
    $[164] = datesPresent;
    $[165] = shouldShowRatesAndAvailability;
    $[166] = t64;
  } else {
    t64 = $[166];
  }
  let t65;
  if ($[167] !== datesPresent || $[168] !== numberOfNights || $[169] !== quoteData) {
    t65 = datesPresent && quoteData && quoteData.bookingQuoteByListingAndDates && quoteData.bookingQuoteByListingAndDates.__typename == "BookingQuote" && quoteData.bookingQuoteByListingAndDates.quoteItems?.length > 0 && quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents && <div className="mt-6 w-full text-primary-main"><ListingQuoteBreakdown numberOfNights={numberOfNights} totalWithoutTaxes={quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents} quoteBreakdownItems={quoteData.bookingQuoteByListingAndDates.quoteItems} /></div>;
    $[167] = datesPresent;
    $[168] = numberOfNights;
    $[169] = quoteData;
    $[170] = t65;
  } else {
    t65 = $[170];
  }
  let t66;
  if ($[171] !== datesPresent || $[172] !== quoteData) {
    t66 = datesPresent && quoteData && quoteData.bookingQuoteByListingAndDates && quoteData.bookingQuoteByListingAndDates.__typename == "BookingQuote" && quoteData.bookingQuoteByListingAndDates.quoteItems?.length == 0 && quoteData.bookingQuoteByListingAndDates.totalCostCents && <div className="mt-6 w-full text-primary-main"><div className="flex w-full justify-between space-x-2 py-2"><Typography component="span" variant="subtitle2">Charge Total</Typography><Typography component="span" variant="subtitle2">{formatWholeDollarAmount(quoteData.bookingQuoteByListingAndDates.totalCostCents)}</Typography></div></div>;
    $[171] = datesPresent;
    $[172] = quoteData;
    $[173] = t66;
  } else {
    t66 = $[173];
  }
  let t67;
  if ($[174] !== datesPresent || $[175] !== quoteData) {
    t67 = datesPresent && quoteData && quoteData.bookingQuoteByListingAndDates && quoteData.bookingQuoteByListingAndDates.__typename == "BookingQuoteUnavailable" && <div className="mt-4 px-2 text-primary-main font-light text-xs lg:text-sm flex flex-col w-full justify-between">This property is not available for your selected dates. Please try different dates.</div>;
    $[174] = datesPresent;
    $[175] = quoteData;
    $[176] = t67;
  } else {
    t67 = $[176];
  }
  let t68;
  if ($[177] !== datesPresent || $[178] !== quoteData) {
    t68 = datesPresent && quoteData && quoteData.bookingQuoteByListingAndDates && quoteData.bookingQuoteByListingAndDates.__typename == "ServiceUnavailableError" && <div className="mt-4 px-2 text-primary-main font-light text-xs lg:text-sm flex flex-col w-full justify-between">{quoteData.bookingQuoteByListingAndDates.message}</div>;
    $[177] = datesPresent;
    $[178] = quoteData;
    $[179] = t68;
  } else {
    t68 = $[179];
  }
  let t69;
  if ($[180] !== datesPresent || $[181] !== quoteValidationMessage) {
    t69 = datesPresent && quoteValidationMessage && <div className="mt-4 px-2 text-primary-main font-light text-xs lg:text-sm flex flex-col w-full justify-between">{quoteValidationMessage}</div>;
    $[180] = datesPresent;
    $[181] = quoteValidationMessage;
    $[182] = t69;
  } else {
    t69 = $[182];
  }
  let t70;
  if ($[183] !== t65 || $[184] !== t66 || $[185] !== t67 || $[186] !== t68 || $[187] !== t69) {
    t70 = <div className="mb-0">{t65}{t66}{t67}{t68}{t69}</div>;
    $[183] = t65;
    $[184] = t66;
    $[185] = t67;
    $[186] = t68;
    $[187] = t69;
    $[188] = t70;
  } else {
    t70 = $[188];
  }
  let t71;
  if ($[189] !== tooltipState.content || $[190] !== tooltipState.isVisible || $[191] !== tooltipState.x || $[192] !== tooltipState.y) {
    t71 = <DayPickerTooltip x={tooltipState.x} y={tooltipState.y} isVisible={tooltipState.isVisible}>{tooltipState.content}</DayPickerTooltip>;
    $[189] = tooltipState.content;
    $[190] = tooltipState.isVisible;
    $[191] = tooltipState.x;
    $[192] = tooltipState.y;
    $[193] = t71;
  } else {
    t71 = $[193];
  }
  let t72;
  if ($[194] !== t20 || $[195] !== t28 || $[196] !== t61 || $[197] !== t63 || $[198] !== t64 || $[199] !== t70 || $[200] !== t71) {
    t72 = <div className="w-full">{t20}{t28}{t61}{t63}{t64}{t70}{t71}</div>;
    $[194] = t20;
    $[195] = t28;
    $[196] = t61;
    $[197] = t63;
    $[198] = t64;
    $[199] = t70;
    $[200] = t71;
    $[201] = t72;
  } else {
    t72 = $[201];
  }
  return t72;
};
function _temp(feature) {
  return feature.key === FeatureKey.PetsNotAllowed;
}
function _temp2(e, setValue, maxValue) {
  e.preventDefault();
  const re = /^[0-9\b]+$/;
  if (e.target.value.startsWith("0")) {
    e.target.value = e.target.value.slice(1);
  }
  if (e.target.value === "" || re.test(e.target.value)) {
    const inputValue = Number(e.target.value);
    const value = inputValue > maxValue ? maxValue : inputValue;
    setValue(value);
  }
}
function _temp3(val) {
  if (val > 1) {
    return val - 1;
  } else {
    return val;
  }
}
function _temp4(val_0) {
  return val_0 >= guestCapacity.ADULTS ? guestCapacity.ADULTS : val_0 + 1;
}
function _temp5(val_1) {
  if (val_1 > 0) {
    return val_1 - 1;
  } else {
    return val_1;
  }
}
function _temp6(val_2) {
  return val_2 >= guestCapacity.CHILDREN ? guestCapacity.CHILDREN : val_2 + 1;
}
function _temp7(val_3) {
  if (val_3 > 0) {
    return val_3 - 1;
  } else {
    return val_3;
  }
}
function _temp8(val_4) {
  if (val_4 > 5) {
    return 5;
  } else {
    return val_4 + 1;
  }
}
function _temp9(val_5) {
  if (val_5 > 0) {
    return val_5 - 1;
  } else {
    return val_5;
  }
}
function _temp10(val_6) {
  if (val_6 > 2) {
    return 2;
  } else {
    return val_6 + 1;
  }
}